import CloseIcon from "@mui/icons-material/Close";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useContext, useEffect, useState } from "react";
import ResponsiveRow from "../../../DashboardComponents/Form/Slides/ResponsiveRow";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import useAdminClients from "../../AdminQueries/useAdminClients";
import Gauge from "./Gauge";
import "./styles/audit-landing.css";

const ProjectName = (clientId, projectName, setProjectName) => {
  return (
    <div className="project-name-container">
      <FormControl
        variant="outlined"
        id="text-form"
        className={"project-form-control"}
        hiddenLabel={false}
      >
        <InputLabel
          id="text-field-label"
          htmlFor="project-name-field"
          classes={{ shrink: "text-field-label-shrink" }}
        >
          Project Name
        </InputLabel>
        <OutlinedInput
          type="text"
          id="text-field"
          value={projectName}
          autoComplete="off"
          label={"Project Name"}
          name="project-name-field"
          classes={{
            root: "text-field-root",
            notchedOutline: "text-notchedOutline",
            input: "text-field-input",
          }}
        />
      </FormControl>
    </div>
  );
};

const AuditLanding = () => {
  const { setHeading, queryClient, isAdmin } = useContext(DashboardContext);
  const { data: clientsData, isFetched: clientsIsFetched } =
    useAdminClients(isAdmin);
  const [auditType, setAuditType] = useState("");
  useEffect(() => {
    setHeading("Audit Report");
  }, []);
  const [selectedClient, setSelectedClient] = useState("");
  const [projectName, setProjectName] = useState("");
  const [safetyQuestions, setSafetyQuestions] = useState([
    {
      question: "Are all workers wearing PPE?:",
      rating: null,
      weight: 0.75,
    },
    {
      question: "Is there a first aid kit on site?:",
      rating: null,
      weight: 0.25,
    },
  ]);
  const [dustQuestions, setDustQuestions] = useState([
    {
      question: "Are all working areas properly watered:",
      rating: null,
      weight: 1,
    },
    {
      question: "Visible track out on streets or sidewalks onsite:",
      rating: null,
      weight: 0.5,
    },
  ]);
  const [swpppQuestions, setSwpppQuestions] = useState([
    {
      question: "Are the curb lines free of sediment:",
      rating: null,
      weight: 0.75,
    },
    {
      question: "Is the perimeter protection in place & functioning:",
      rating: null,
      weight: 0.9,
    },
    {
      question: "Is the site free of trash & debris:",
      rating: null,
      weight: 0.25,
    },
  ]);
  return (
    <div id="audit-landing-container">
      <div id="main-audit-form">
        <header className="audit-header">
          <h1 id="audit-heading">Create Audit Report</h1>
        </header>
        <p className="important-text">
          Please select a client & audit type to begin the audit inspection
        </p>
        <div className="audit-form">
          <h2 className="question-section">Audit Type</h2>
          <ToggleButtonGroup
            id="audit-type-toggle"
            className="audit-type-toggle"
            value={auditType}
            exclusive
            onChange={(e, value) => {
              if (value !== null) {
                setAuditType(value);
              } else {
                setAuditType("");
              }
            }}
          >
            <ToggleButton
              id="audit-type-toggle-button"
              className="audit-type-toggle-button"
              value="swppp"
              selected={auditType === "swppp"}
            >
              SWPPP
            </ToggleButton>
            <ToggleButton
              id="audit-type-toggle-button"
              className="audit-type-toggle-button"
              value="dust"
              selected={auditType === "dust"}
            >
              Dust
            </ToggleButton>
            <ToggleButton
              id="audit-type-toggle-button"
              className="audit-type-toggle-button"
              value="safety"
              selected={auditType === "safety"}
            >
              Safety
            </ToggleButton>
          </ToggleButtonGroup>
          {auditType !== "" && (
            <>
              <h2 className="question-section">Client & Project</h2>
              <ResponsiveRow type="center">
                <FormControl
                  id="select-form"
                  classes={{ root: "clients-form-root" }}
                >
                  <InputLabel
                    id="select-label"
                    classes={{ shrink: "select-label-shrink" }}
                  >
                    Select a Client
                  </InputLabel>
                  <Select
                    id="select"
                    className="clients-select"
                    variant="standard"
                    value={selectedClient || ""}
                    SelectDisplayProps={{
                      className: "clients-select-display",
                    }}
                    MenuProps={{
                      className: "clients-select-menu",
                      classes: {
                        list: "clients-select-list",
                        paper: "clients-select-paper",
                      },
                    }}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value === 0) {
                        setSelectedClient("");
                      } else {
                        setSelectedClient(value);
                      }
                    }}
                    classes={{
                      root: "select-root",
                      select: "clients-select-root-select",
                      icon: "clients-select-root-icon",
                      nativeInput: "select-nativeInput",
                    }}
                  >
                    <MenuItem
                      key="clear-client"
                      className="select-menu-item"
                      value={0}
                      id="clear-item"
                      divider={true}
                      classes={{
                        root: "list-item",
                        selected: "list-item-selected",
                        divider: "clear-item-divider",
                      }}
                    >
                      Clear Selection
                      <CloseIcon id="clear-client-icon" />
                    </MenuItem>
                    {clientsIsFetched &&
                      clientsData.map((client, idx) => (
                        <MenuItem
                          key={client.id}
                          className="select-menu-item"
                          value={client.id}
                          id={"item"}
                          divider={
                            idx === clientsData.length - 1 ? false : true
                          }
                          classes={{
                            root: "list-item",
                            selected: "list-item-selected",
                            divider: "list-item-divider",
                          }}
                        >
                          {client.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <ProjectName
                  clientId={selectedClient}
                  projectName={projectName}
                  setProjectName={setProjectName}
                />
              </ResponsiveRow>
              <ResponsiveRow type="center">
                <Gauge value={95} label="Recommended Score" />
                <Gauge value={60} label="Recommended Score" />
                <Gauge value={40} label="Recommended Score" />
                <Gauge value={20} label="Recommended Score" />
              </ResponsiveRow>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLanding;
